import React, { useEffect, useState, useRef } from 'react';

/**
 * ImportProgressIndicator - A rich, multi-phase import progress display
 * Shows real-time category counts during X bookmark import with smooth animations
 * Renders as a centered modal overlay during import
 */
const ImportProgressIndicator = ({ 
  importProgress, 
  importing, 
  onCancel,
  cancelling,
  isDarkMode = false
}) => {
  // Smooth counter animation state
  const [displayCounts, setDisplayCounts] = useState({
    imports: 0,
    ideas: 0,
    curations: 0,
    polls: 0,
    requests: 0,
    received: 0
  });
  
  // Track previous values for smooth interpolation
  const targetCountsRef = useRef(displayCounts);
  const animationFrameRef = useRef(null);
  
  // Track "stuck" state - when progress hasn't changed for a while
  const [stuckMessage, setStuckMessage] = useState(null);
  const lastProcessedRef = useRef(0);
  const stuckTimerRef = useRef(null);
  
  // Humorous messages for when processing takes a while (shown after 5 seconds)
  const stuckMessages = [
    "Still working on it...",
    "This one's a thinker... 🤔",
    "Crunching some serious numbers here...",
    "Sorry, this is a tough one!",
    "AI is having a deep think...",
    "Complex thread detected! Bear with us..."
  ];
  
  // Detect when progress is stuck
  useEffect(() => {
    const currentProcessed = importProgress?.processed || 0;
    const status = importProgress?.status;
    
    // Clear message if complete or not importing
    if (status === 'complete' || !importing) {
      setStuckMessage(null);
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
      return;
    }
    
    // If progress changed, reset the stuck timer and clear message
    if (currentProcessed !== lastProcessedRef.current) {
      lastProcessedRef.current = currentProcessed;
      setStuckMessage(null);
      
      // Clear existing timer
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
      }
      
      // Start new timer - pick ONE random message after 5 seconds
      stuckTimerRef.current = setTimeout(() => {
        const randomMsg = stuckMessages[Math.floor(Math.random() * stuckMessages.length)];
        setStuckMessage(randomMsg);
      }, 5000);
    }
    
    return () => {
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
      }
    };
  }, [importProgress?.processed, importProgress?.status, importing]);
  
  // Animate counters smoothly
  useEffect(() => {
    if (!importProgress?.categories) return;
    
    targetCountsRef.current = importProgress.categories;
    
    // Smooth animation loop
    const animate = () => {
      setDisplayCounts(prev => {
        const next = { ...prev };
        let needsUpdate = false;
        
        Object.keys(targetCountsRef.current).forEach(key => {
          const target = targetCountsRef.current[key] || 0;
          const current = prev[key] || 0;
          
          if (current !== target) {
            needsUpdate = true;
            // Smooth easing - move 20% of the way each frame
            const diff = target - current;
            if (Math.abs(diff) < 0.5) {
              next[key] = target;
            } else {
              next[key] = Math.round(current + diff * 0.2);
            }
          }
        });
        
        if (needsUpdate) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
        
        return next;
      });
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [importProgress?.categories]);
  
  // Don't render if not importing
  if (!importing) return null;
  
  const status = importProgress?.status || 'idle';
  const phase = importProgress?.phase || 'fetching';
  const total = importProgress?.total || 0;
  const processed = importProgress?.processed || 0;
  const analyzing = importProgress?.analyzing || {};
  
  // Calculate progress percentage
  const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;
  
  // Category configuration with colors for light and dark mode
  const categories = [
    { 
      key: 'imports', 
      label: 'BOOKMARKS', 
      color: 'text-blue-500',
      darkColor: 'text-blue-400',
      bgColor: isDarkMode ? 'bg-blue-900/40' : 'bg-blue-50',
      value: displayCounts.imports || total
    },
    { 
      key: 'ideas', 
      label: 'IDEAS', 
      color: 'text-green-500',
      darkColor: 'text-green-400',
      bgColor: isDarkMode ? 'bg-green-900/40' : 'bg-green-50',
      value: displayCounts.ideas,
      analyzing: analyzing.threads
    },
    { 
      key: 'curations', 
      label: 'CURATIONS', 
      color: 'text-purple-500',
      darkColor: 'text-purple-400',
      bgColor: isDarkMode ? 'bg-purple-900/40' : 'bg-purple-50',
      value: displayCounts.curations,
      analyzing: analyzing.threads
    },
    { 
      key: 'polls', 
      label: 'POLLS', 
      color: 'text-orange-500',
      darkColor: 'text-orange-400',
      bgColor: isDarkMode ? 'bg-orange-900/40' : 'bg-orange-50',
      value: displayCounts.polls,
      analyzing: analyzing.polls
    },
    { 
      key: 'requests', 
      label: 'REQUESTS', 
      color: 'text-pink-500',
      darkColor: 'text-pink-400',
      bgColor: isDarkMode ? 'bg-pink-900/40' : 'bg-pink-50',
      value: displayCounts.requests,
      analyzing: analyzing.requests
    },
    { 
      key: 'received', 
      label: 'RECEIVED', 
      color: 'text-cyan-500',
      darkColor: 'text-cyan-400',
      bgColor: isDarkMode ? 'bg-cyan-900/40' : 'bg-cyan-50',
      value: displayCounts.received
    }
  ];
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      data-testid="import-progress-overlay"
    >
      <div 
        className={`rounded-2xl shadow-2xl overflow-hidden w-full max-w-lg mx-4 transform transition-all ${
          isDarkMode 
            ? 'bg-slate-800 border border-slate-700' 
            : 'bg-white border border-slate-200'
        }`}
        data-testid="import-progress-indicator"
      >
        {/* Header with phase indicator */}
        <div className={`px-6 py-5 text-white ${
          isDarkMode 
            ? 'bg-gradient-to-r from-slate-700 to-slate-600' 
            : 'bg-gradient-to-r from-blue-600 to-blue-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Spinner */}
              {status !== 'complete' && (
                <div className="relative">
                  <div className="animate-spin rounded-full h-8 w-8 border-3 border-white border-t-transparent"></div>
                </div>
              )}
              {status === 'complete' && (
                <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              
              {/* Phase text */}
              <div>
                <div className="font-semibold text-lg">
                  {phase === 'fetching' ? 'Importing bookmarks...' : 
                   phase === 'analyzing' ? 'Finding tickers...' :
                   phase === 'organizing' ? 'Organizing into folders...' :
                   phase === 'complete' ? 'Import complete!' :
                   'Processing...'}
                </div>
                <div className={`text-sm mt-0.5 ${isDarkMode ? 'text-slate-300' : 'text-blue-200'}`}>
                  {status === 'complete' ? 
                    `${displayCounts.ideas + displayCounts.curations} ideas found` :
                    phase === 'organizing' ?
                    'Almost done!' :
                    stuckMessage ? 
                    stuckMessage :
                    `${processed} of ${total} processed`
                  }
                </div>
              </div>
            </div>
            
            {/* Progress percentage badge */}
            <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
              isDarkMode ? 'bg-slate-600' : 'bg-white/20'
            }`}>
              {progressPercent}%
            </div>
          </div>
          
          {/* Progress bar */}
          <div className={`mt-4 h-2 rounded-full overflow-hidden ${
            isDarkMode ? 'bg-slate-600' : 'bg-blue-500/30'
          }`}>
            <div 
              className={`h-full rounded-full transition-all duration-300 ease-out ${
                isDarkMode ? 'bg-blue-400' : 'bg-white'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
        
        {/* Category grid - 6 columns with colored numbers */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-6 gap-2">
            {categories.map((cat) => (
              <div 
                key={cat.key}
                className={`flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-all duration-200 ${cat.bgColor}`}
              >
                {/* Number with analyzing indicator */}
                <div className="relative">
                  <span 
                    className={`text-3xl font-bold tabular-nums transition-all duration-200 ${
                      isDarkMode ? cat.darkColor : cat.color
                    }`}
                    style={{ 
                      textShadow: isDarkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
                      minWidth: '2ch',
                      display: 'inline-block',
                      textAlign: 'center'
                    }}
                  >
                    {cat.value}
                  </span>
                  
                  {/* Analyzing pulse indicator */}
                  {cat.analyzing && status !== 'complete' && (
                    <span className="absolute -top-1 -right-2 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                    </span>
                  )}
                </div>
                
                {/* Label */}
                <span 
                  className={`text-[9px] font-semibold uppercase tracking-wider mt-1 ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  {cat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer with cancel button */}
        {status !== 'complete' && (
          <div className="px-6 pb-5 flex justify-center">
            <button
              onClick={onCancel}
              disabled={cancelling}
              className={`text-sm transition-colors disabled:opacity-50 px-4 py-2 ${
                isDarkMode 
                  ? 'text-slate-500 hover:text-red-400' 
                  : 'text-slate-400 hover:text-red-500'
              }`}
            >
              {cancelling ? 'Cancelling...' : 'Cancel import'}
            </button>
          </div>
        )}
        
        {/* Completion state */}
        {status === 'complete' && (
          <div className="px-6 pb-5 text-center">
            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Refreshing your dashboard...
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportProgressIndicator;
